import { useEffect, useState } from 'react';

export type Platform =
  | { kind: 'detecting' }
  | { kind: 'apple_silicon_mac' }
  | { kind: 'intel_mac' }
  | { kind: 'windows' }
  | { kind: 'non_mac' }
  | { kind: 'unknown' };

type NavigatorWithUAData = Navigator & {
  userAgentData?: {
    getHighEntropyValues(
      hints: string[]
    ): Promise<{ architecture?: string; platform?: string }>;
  };
};

const MAC_RENDERER_APPLE_RE = /apple/i;
const MAC_RENDERER_INTEL_AMD_RE = /intel|amd/i;

function getWebGLRenderer(): string | null {
  try {
    const canvas = document.createElement('canvas');
    const rawGl =
      canvas.getContext('webgl') ?? canvas.getContext('experimental-webgl');
    if (!rawGl) {
      return null;
    }
    const gl = rawGl as WebGLRenderingContext;
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    if (!ext) {
      return null;
    }
    return gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) as string;
  } catch {
    return null;
  }
}

export async function detectPlatform(): Promise<
  Exclude<Platform, { kind: 'detecting' }>
> {
  const nav = navigator as NavigatorWithUAData;

  if (nav.userAgentData?.getHighEntropyValues) {
    try {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('platform-detection-timeout')), 3000)
      );
      const values = await Promise.race([
        nav.userAgentData.getHighEntropyValues(['architecture', 'platform']),
        timeoutPromise,
      ]);
      const { platform = '', architecture = '' } = values;
      if (platform === 'macOS') {
        if (architecture === 'arm') {
          return { kind: 'apple_silicon_mac' };
        }
        if (architecture === 'x86') {
          return { kind: 'intel_mac' };
        }
        return { kind: 'unknown' };
      }
      if (platform === 'Windows') {
        return { kind: 'windows' };
      }
      return { kind: 'non_mac' };
    } catch (err) {
      if (
        err instanceof Error &&
        err.message === 'platform-detection-timeout'
      ) {
        console.warn(
          '[DesktopInstall] getHighEntropyValues timed out, falling back to legacy detection'
        );
      }
      // Fall through to legacy detection
    }
  }

  const legacyPlatform = navigator.platform.toLowerCase();

  if (legacyPlatform.includes('mac')) {
    // iPadOS Safari masquerades as Mac (navigator.platform === 'MacIntel'),
    // but exposes multi-touch — desktop Macs report maxTouchPoints <= 1 even
    // with a trackpad. Treat touch-capable "Macs" as non-Mac so iPad users
    // don't see the Apple Silicon desktop install state.
    if ((navigator.maxTouchPoints ?? 0) > 1) {
      return { kind: 'non_mac' };
    }
    const renderer = getWebGLRenderer();
    if (renderer !== null) {
      if (MAC_RENDERER_APPLE_RE.test(renderer)) {
        return { kind: 'apple_silicon_mac' };
      }
      if (MAC_RENDERER_INTEL_AMD_RE.test(renderer)) {
        return { kind: 'intel_mac' };
      }
    }
    return { kind: 'unknown' };
  }

  if (legacyPlatform.includes('win')) {
    return { kind: 'windows' };
  }

  return { kind: 'non_mac' };
}

export function useDetectPlatform(): Platform {
  const [platform, setPlatform] = useState<Platform>({ kind: 'detecting' });

  useEffect(() => {
    let cancelled = false;
    detectPlatform()
      .then(result => {
        if (!cancelled) {
          setPlatform(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPlatform({ kind: 'unknown' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return platform;
}
